import { TextField, TextFieldProps, useTheme } from "@mui/material";
import { TextFieldVariants } from "@mui/material/TextField/TextField";
import { ChangeEvent } from "react";
import { mergeSx } from "src/utils/styles";

export type AavoTextFieldProps<Variant extends TextFieldVariants = TextFieldVariants> = Omit<
	TextFieldProps<Variant>,
	"onSubmit" | "onChange" | "error"
> & {
	onChange?: (value: string, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	onSubmit?: (value: string) => void;
	error?: string | boolean;
};

export const AavoTextField = ({
	onSubmit,
	onKeyDown,
	onBlur,
	onChange,
	onFocus,
	sx,
	multiline,
	error: errorProp,
	helperText: helperTextProp,
	...other
}: AavoTextFieldProps) => {
	const theme = useTheme();
	const placeholderOpacity = 0.5;
	const placeholderColor = theme.palette.text.primary;

	const error = typeof errorProp === "string" ? errorProp != "" : errorProp;
	const helperText = helperTextProp ?? (typeof errorProp === "string" ? errorProp : undefined);

	return (
		<TextField
			onKeyDown={async (e) => {
				onKeyDown?.(e);
				if (e.key === "Enter") {
					// Multiline text field can be submitted with Ctrl+Enter
					if (multiline && !e.ctrlKey) return;

					const input = (e as any).target.value;
					onSubmit?.(input);
				}
			}}
			onBlur={async (e) => {
				onBlur?.(e);
				onSubmit?.(e.target.value);
			}}
			onChange={(e) => {
				onChange?.(e.target.value, e);
			}}
			onFocus={(e) => {
				onFocus?.(e);
				// Move cursor to the end of the input field
				e.currentTarget.setSelectionRange(
					e.currentTarget.value.length,
					e.currentTarget.value.length,
				);
			}}
			error={error}
			helperText={helperText}
			multiline={multiline}
			sx={mergeSx(
				{
					input: {
						color: placeholderColor,
						"&::placeholder": {
							opacity: placeholderOpacity,
						},
					},
					label: {
						color: placeholderColor,
						opacity: placeholderOpacity,
					},
				},
				sx,
			)}
			{...other}
		/>
	);
};
